/**
 * Element dimensions mixin.
 * Uses native ResizeObserver or injected children listening for scroll events.
 * Adds "dimensions" reactive property.
 * @template { import('@nrk/reactive-element').ReactiveElementBase } BaseClass
 * @param { BaseClass } Base
 */
export function DimensionsMixin(Base) {
  return class extends Base {
    static get reactiveProperties() {
      return {
        ...super.reactiveProperties,
        dimensions: Object,
      };
    }

    /**
     * Constructor
     * @param  {...any} args
     */
    constructor(...args) {
      super(...args);
      /** @type { import('./index.js').Dimensions } - The "height" and "width" in pixels */
      this.dimensions = { height: 0, width: 0 };
      /** @type { ResizeObserver | undefined } */
      this._resizeObserver = undefined;
    }

    /**
     * @param { import('@nrk/reactive-element').ChangedProperties } changedProperties
     */
    firstUpdated(changedProperties) {
      super.firstUpdated && super.firstUpdated(changedProperties);
      this._initResizeObserver();
    }

    disconnectedCallback() {
      this._resizeObserver && this._resizeObserver.disconnect();
      super.disconnectedCallback && super.disconnectedCallback();
    }

    _initResizeObserver() {
      if (typeof window.ResizeObserver === 'undefined') {
        throw Error('ResizeObserver not supported on this platform');
      }

      /**
       * @param { Array<ResizeObserverEntry> } entries
       */
      const callback = (entries) => {
        for (const entry of entries) {
          // New contentBoxSize API
          // Ensure blockSize and inlineSize are defined due to Chrome bug.
          const contentBoxSize = entry.contentBoxSize ? entry.contentBoxSize[0] : undefined;

          if (contentBoxSize) {
            this.dimensions = {
              height: contentBoxSize.blockSize,
              width: contentBoxSize.inlineSize,
            }; // Legacy contentRect API
          } else {
            this.dimensions = {
              height: entry.contentRect.height,
              width: entry.contentRect.width,
            };
          }
        }
      };

      this._resizeObserver = new ResizeObserver(callback);
      this._resizeObserver.observe(this);
    }
  };
}
